import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as userStore from '@cartwhl/common/stores/userStore';
import { getStudioUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import FormattedDate from '@cartwhl/ui/atoms/FormattedDate';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import TextNew from '@cartwhl/ui/base/TextNew';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';
import EmptyState from '@cartwhl/ui/messaging/EmptyState';

const FETCH_LIMIT = 8;

type SortBy = 'Newest' | 'Oldest';
type FileType = 'All' | 'BVH' | 'FBX' | 'glTF' | 'USD' | 'Maya';

const mapExportTypeToFileType = (exportType: string): FileType => {
  switch (exportType) {
    case 'BVH':
      return 'BVH';
    case 'FBX-BLENDER':
    case 'FBX-MAYA':
    case 'FBX-UNREAL':
    case 'FBX-ROBLOX':
    case 'FBX-UNITY':
      return 'FBX';
    case 'GLB':
      return 'glTF';
    case 'USDZ':
      return 'USD';
    case 'MA':
    case 'MB':
      return 'Maya';
    default:
      return 'All';
  }
};

export default function DashboardDownloadHistory() {
  const { downloadHistory, loading: userLoading } = useSnapshot(
    userStore.state,
  );

  const [selectedFileType, setSelectedFileType] = useState<FileType>('All');
  const [sortAscending, setSortAscending] = useState(false);

  useEffect(() => {
    if (downloadHistory.status === 'initial') {
      userStore.fetchDownloadHistory({ limit: FETCH_LIMIT });
    }
  }, [downloadHistory.status]);

  const handleSortChange = (sortBy: SortBy) => {
    const nextSortAscending = sortBy === 'Oldest';
    setSortAscending(nextSortAscending);
    userStore.fetchDownloadHistory({
      limit: FETCH_LIMIT,
      sortAscending: nextSortAscending,
    });
  };

  const handleFileTypeChange = (fileType: FileType) => {
    setSelectedFileType(fileType);
  };

  const filteredRecords = downloadHistory.data?.items
    .filter((record) => {
      if (selectedFileType === 'All') return true;
      return mapExportTypeToFileType(record.exportType) === selectedFileType;
    })
    .sort((a, b) => {
      if (sortAscending) {
        return (
          new Date(a.dateRequested).getTime() -
          new Date(b.dateRequested).getTime()
        );
      }
      return (
        new Date(b.dateRequested).getTime() -
        new Date(a.dateRequested).getTime()
      );
    });

  if (
    userLoading ||
    (downloadHistory.status === 'pending' &&
      downloadHistory.data?.items.length === 0)
  ) {
    return (
      <section className="mx-auto flex w-full max-w-(--breakpoint-md) flex-col gap-5">
        <TextNew
          as="h1"
          token="display/20/medium"
          leading="headline"
          className="text-primary text-pretty"
        >
          Download history
        </TextNew>
        <div className="border-primary flex h-[210px] items-center justify-center rounded-lg border p-6">
          <Loading className="text-gray-30" />
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-(--breakpoint-md) flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TextNew
          as="h1"
          token="display/20/medium"
          leading="headline"
          className="text-primary text-pretty"
        >
          Download history
        </TextNew>
        <Dropdown trigger={<Button variant="tertiary" icon={<SortIcon />} />}>
          <Text
            as="div"
            variant="web/body/md"
            className="px-4 py-2 opacity-30 dark:opacity-50"
          >
            Sort by
          </Text>
          <Dropdown.Item onClick={() => handleSortChange('Newest')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  !sortAscending ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              Newest
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleSortChange('Oldest')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  sortAscending ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              Oldest
            </div>
          </Dropdown.Item>
          <Text
            as="div"
            variant="web/body/md"
            className="px-4 py-2 opacity-30 dark:opacity-50"
          >
            Filter by
          </Text>
          <Dropdown.Item onClick={() => handleFileTypeChange('All')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  selectedFileType === 'All' ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              All
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleFileTypeChange('BVH')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  selectedFileType === 'BVH' ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              BVH
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleFileTypeChange('FBX')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  selectedFileType === 'FBX' ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              FBX
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleFileTypeChange('glTF')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  selectedFileType === 'glTF' ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              glTF
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleFileTypeChange('USD')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  selectedFileType === 'USD' ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              USD
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleFileTypeChange('Maya')}>
            <div>
              <CheckmarkIcon
                className={`mr-2 inline ${
                  selectedFileType === 'Maya' ? 'opacity-100' : 'opacity-0'
                }`}
              />{' '}
              Maya
            </div>
          </Dropdown.Item>
        </Dropdown>
      </div>

      <div className="divide-gray-15 dark:divide-gray-60 divide-y">
        {filteredRecords?.length === 0 ? (
          <EmptyState
            description={
              selectedFileType === 'All'
                ? 'Downloads will appear here'
                : 'No downloads of this type, try changing your filters'
            }
          />
        ) : (
          filteredRecords?.map((record, i) => (
            <div key={`download-${i}`} className="relative gap-4 py-4">
              <div
                className="hover:bg-gray-15 dark:hover:bg-gray-75 group relative flex cursor-pointer gap-4 rounded-lg p-4"
                onClick={() => {
                  const studioURL = `${getStudioUrl(true)}/motion/${record.projectID}`;
                  window.open(studioURL, '_blank');
                }}
              >
                <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg">
                  <LazyVideo src={record.mp4Path} />
                </div>
                <div className="flex flex-1 flex-col gap-1 py-3">
                  <Text as="p" variant="web/body/md" className="font-medium">
                    {record.projectName.length > 100
                      ? record.projectName.slice(0, 100) + '…'
                      : record.projectName}
                  </Text>
                  <Text
                    as="p"
                    variant="web/body/md"
                    className="opacity-30 dark:opacity-50"
                  >
                    {record.exportType}
                  </Text>
                  <Text
                    as="p"
                    variant="web/body/md"
                    className="opacity-30 dark:opacity-50"
                  >
                    {record.characterName}
                  </Text>
                </div>
                <div
                  className="flex gap-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text
                    as="p"
                    variant="web/body/md"
                    className="py-3 opacity-30 dark:opacity-50"
                  >
                    <FormattedDate date={record.dateRequested} />
                  </Text>
                  <Dropdown
                    trigger={
                      <Button
                        variant="secondary"
                        className="visible group-hover:visible sm:invisible"
                        icon={<ThreeDotsIcon />}
                      />
                    }
                  >
                    <Dropdown.Item
                      onClick={() => {
                        const studioURL = `${getStudioUrl(true)}/${record.projectType.toLowerCase()}/${record.projectID}`;
                        window.open(studioURL, '_blank');
                      }}
                    >
                      Open
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        window.open(record.downloadPath, '_blank');
                      }}
                    >
                      Download
                    </Dropdown.Item>
                  </Dropdown>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {downloadHistory.data?.nextToken && (
        <div className="container my-10 flex justify-center">
          <InfiniteLoadingTrigger
            loading={downloadHistory.status === 'pending'}
            onEnter={() =>
              userStore.fetchDownloadHistory({
                limit: FETCH_LIMIT,
                nextToken: downloadHistory.data?.nextToken,
                append: true,
                sortAscending,
              })
            }
            done={!downloadHistory.data?.nextToken}
          />
        </div>
      )}
    </section>
  );
}
